import api from '../../../shared/utils/api';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface NpsStatus {
  shouldShow: boolean;
  reason?: string;
  activatedDaysAgo?: number;
  hasResponded?: boolean;
  lastSubmittedAt?: string;
  lastDismissedAt?: string;
}

export interface NpsSubmission {
  score: number;
  comment?: string;
}

export interface CsatSubmission {
  entityType: string;
  entityId: string;
  rating: number;
}

export interface NpsResponse {
  id: string;
  userId?: string;
  tenantId?: string;
  score: number;
  comment?: string;
  createdAt: string;
}

export interface NpsSummary {
  nps_score: number;
  total_responses: number;
  promoters: number;
  passives: number;
  detractors: number;
  promoter_percentage: number;
  passive_percentage: number;
  detractor_percentage: number;
  period_days: number;
  target_nps: number;
  target_respondents: number;
  meets_target: boolean;
}

/**
 * O que `GET /nps/stats` de fato serve: as três metas (`target_*`,
 * `meets_target`) são SINTETIZADAS por este cliente quando o backend não as
 * manda — por isso são opcionais aqui e obrigatórias em `NpsSummary`.
 */
type NpsStatsPayload = Omit<NpsSummary, 'target_nps' | 'target_respondents' | 'meets_target'> &
  Partial<Pick<NpsSummary, 'target_nps' | 'target_respondents' | 'meets_target'>>;

export interface NpsTrendPoint {
  period: string;
  nps_score: number;
  total_responses: number;
}

export interface NpsDetractor {
  id: string;
  score: number;
  feedback_text?: string;
  category?: string;
  created_at: string;
}

// ─── API (aligned with janus-bff sales/nps.routes.ts) ────────────────────────

export const npsApiV2 = {
  /** GET /nps/survey-status — check if the current user should see the NPS modal */
  getStatus: async (): Promise<NpsStatus> => {
    const { data } = await api.get<NpsStatus>('/nps/survey-status');
    return data;
  },

  /** POST /nps/submit — submit an NPS score (0-10) with an optional comment */
  submitResponse: async (score: number, comment?: string): Promise<NpsResponse> => {
    const { data } = await api.post<NpsResponse>('/nps/submit', { score, comment });
    return data;
  },

  /** POST /nps/csat — submit CSAT rating */
  submitCsat: async (entityType: string, entityId: string, rating: number): Promise<void> => {
    await api.post('/nps/csat', { entityType, entityId, rating });
  },

  /** POST /nps/dismiss — dismiss the NPS survey */
  dismissSurvey: async (): Promise<void> => {
    await api.post('/nps/dismiss');
  },

  /** POST /nps/track-session — track session for NPS trigger logic */
  trackSession: async (): Promise<void> => {
    await api.post('/nps/track-session');
  },

  /** GET /nps/stats — aggregated NPS statistics (admin/gestor) */
  getSummary: async (days = 90): Promise<NpsSummary> => {
    const { data } = await api.get<NpsStatsPayload>('/nps/stats', { params: { days } });
    return {
      ...data,
      target_nps: data.target_nps ?? 35,
      target_respondents: data.target_respondents ?? 20,
      meets_target: data.meets_target ?? ((data.nps_score ?? 0) >= 35),
    };
  },

  /** GET /nps/detractors — list NPS detractor entries, used as responses list */
  getResponses: async (params?: { limit?: number; offset?: number; days?: number }): Promise<NpsResponse[]> => {
    const { data } = await api.get<{ detractors?: NpsDetractor[] } | NpsDetractor[]>(
      '/nps/detractors',
      { params },
    );
    const entries = Array.isArray(data) ? data : (data.detractors ?? []);
    return entries.map((e: NpsDetractor) => ({
      id: e.id,
      score: e.score,
      comment: e.feedback_text,
      createdAt: e.created_at,
    }));
  },

  /** Build a basic NPS trend from rolling stat windows */
  getTrend: async (days = 180): Promise<NpsTrendPoint[]> => {
    try {
      const windows = [30, 60, 90, 120, 150, 180].filter((w) => w <= days);
      const results = await Promise.all(
        windows.map(async (w) => {
          try {
            const { data } = await api.get<{ nps_score: number; total_responses: number }>(
              '/nps/stats',
              { params: { days: w } },
            );
            return {
              period: `${w}d`,
              nps_score: data.nps_score ?? 0,
              total_responses: data.total_responses ?? 0,
            };
          } catch {
            return null;
          }
        }),
      );
      return results.filter((r): r is NpsTrendPoint => r !== null);
    } catch {
      return [];
    }
  },
};

/** Legacy alias — prefer npsApiV2 */
export const npsApi = {
  submitNps: npsApiV2.submitResponse,
  submitCsat: npsApiV2.submitCsat,
  getSurveyStatus: npsApiV2.getStatus,
  dismissSurvey: npsApiV2.dismissSurvey,
  trackSession: npsApiV2.trackSession,
  getStats: npsApiV2.getSummary,
  getDetractors: npsApiV2.getResponses,
};
